import { useCallback, useEffect, useRef } from 'react';
import {
  resizingChanged,
  selectResizing,
} from 'features/layout/features/contentDimensions/contentDimensionsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

// Cross-origin iframes (embedded videos) swallow the document-level mousemove/mouseup
// that react-resizable's drag tracking relies on, freezing the drag (SAMBA-9212).
const IFRAME_POINTER_EVENTS_GUARD = { '& iframe': { pointerEvents: 'none' } };

// Consumers spread resizeGuardSx into the resized content's sx.
export const useResizeDragGuard = () => {
  const dispatch = useAppDispatch();
  const isResizing = useAppSelector(selectResizing);
  const resizeGuardSx = isResizing ? IFRAME_POINTER_EVENTS_GUARD : null;

  const isDraggingRef = useRef(false);

  const onResizeStart = useCallback(() => {
    isDraggingRef.current = true;
    dispatch(resizingChanged(true));
  }, [dispatch]);

  const onResizeStop = useCallback(() => {
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;
    dispatch(resizingChanged(false));
  }, [dispatch]);

  // react-resizable only ends the drag on a mouseup it actually receives. Releasing the button
  // outside the window never delivers one, and the flag now disarms every iframe in the room,
  // so the drag needs a way out that does not depend on that event.
  useEffect(() => {
    // mouseup, not pointerup: pointerup completes before the compatibility mouse event that
    // react-resizable listens for, so clearing the flag there re-arms the iframes mid-gesture.
    window.addEventListener('blur', onResizeStop);
    window.addEventListener('mouseup', onResizeStop);
    window.addEventListener('pointercancel', onResizeStop);

    return () => {
      window.removeEventListener('blur', onResizeStop);
      window.removeEventListener('mouseup', onResizeStop);
      window.removeEventListener('pointercancel', onResizeStop);
      onResizeStop();
    };
  }, [onResizeStop]);

  return { resizeGuardSx, onResizeStart, onResizeStop };
};
